// The third answer to spouse-pass's shape-1 question — the spouse is
// real, but not in the system yet, so neither existing button fits.
//
// spouse-pass shape 1 asks "Does <Name> belong in a partner's
// household?" with two answers: search-and-merge (yes, the partner is
// already in the system somewhere) or "No — <Name> is on their own"
// (lives-alone-answer.ts). Ryan hit the gap those two don't cover
// (2026-08-25): a brand-new prospect who told him his marital status and
// his wife's details, where the wife has never been entered as her own
// contact. She isn't "on her own" — Kevin isn't single — and there's
// nothing to search for, because she doesn't exist in the system at all
// yet. The right answer is to add her as a new household member, which
// the household page already supports (AddMemberForm); this flag's decide
// screen just never offered it.
//
// Deliberately the same slug + single-household-ref gate as
// lives-alone-answer.ts: the shared-phone shape already names a
// candidate household to merge into, so "add a new person" is never the
// right offer there.
//
// Root-safe (imports nothing) so it's unit-tested from tests/web/ without
// dragging web/src/api.ts (DOM-typed) into the root tsconfig program —
// same convention as the sibling predicate files in this directory.
//
// HELP-OK: this adds one more inline-explained button to an existing
// flag (spouse-pass) — no dedicated help article covers per-detector
// decide-rail buttons the way cleanup-basics covers the general Cleanup
// flow; only whole distinct workflows (IVANS carrier items, retention
// disposition) get their own article. The button's own label + the
// consequence text under it explain the action in place, per Cleanup UX
// rule 9 ("plain words, help on tap").

export interface AddHouseholdMemberIssueLike {
  detectorSlug?: string | undefined;
  affectedRecords?: { kind: string; id: string; label: string }[] | undefined;
}

export interface AddHouseholdMemberAnswer {
  householdId: string;
  householdLabel: string;
}

const SLUGS = new Set(["spouse-pass"]);

export function addHouseholdMemberAnswer(
  issue: AddHouseholdMemberIssueLike,
): AddHouseholdMemberAnswer | null {
  if (!issue.detectorSlug || !SLUGS.has(issue.detectorSlug)) return null;
  const householdRefs = (issue.affectedRecords ?? []).filter(
    (r) => r.kind === "household",
  );
  if (householdRefs.length !== 1) return null;
  const household = householdRefs[0]!;
  return { householdId: household.id, householdLabel: household.label };
}
